import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { forgotPassword } from '../lib/api';
import { toast } from 'sonner';
import { Mail, ArrowLeft, CheckCircle, Send } from 'lucide-react';
import AuthLayout, { authInputClass } from '../components/AuthLayout';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await forgotPassword(email);
      setSent(true);
      toast.success('Reset link sent! Check your email.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reset link');
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <AuthLayout testId="forgot-password-page">
        <div className="text-center" data-testid="forgot-password-sent">
          <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <CheckCircle size={32} className="text-emerald-500" />
          </div>
          <h1 className="text-xl font-extrabold text-slate-900 mb-2">Check Your Email</h1>
          <p className="text-sm text-slate-600 mb-1">We've sent a password reset link to</p>
          <p className="text-sm font-semibold text-slate-800 mb-4 break-all">{email}</p>
          <p className="text-xs text-slate-500 mb-5">
            Didn't receive the email? Check your spam folder or try again.
          </p>
          <div className="space-y-3">
            <button
              onClick={() => setSent(false)}
              className="w-full h-12 rounded-2xl font-bold text-blue-600 border-2 border-blue-500/70 bg-white hover:bg-blue-50 active:scale-[0.99] transition-all"
            >
              Try Different Email
            </button>
            <button
              onClick={() => navigate('/login')}
              className="w-full h-12 rounded-2xl font-bold text-base text-white flex items-center justify-center gap-2 bg-gradient-to-r from-[#57B22E] to-[#3E9A1C] shadow-lg shadow-green-600/30 hover:from-[#4CA327] hover:to-[#358916] active:scale-[0.99] transition-all"
            >
              Back to Login
            </button>
          </div>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout testId="forgot-password-page">
      <div className="text-center mb-4">
        <h1 className="text-xl font-extrabold text-slate-900">Forgot password?</h1>
        <p className="text-sm text-slate-500 mt-0.5">No worries, we'll send you reset instructions</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4" data-testid="forgot-password-form">
        <div>
          <label className="block text-sm font-bold text-slate-800 mb-1.5">Email Address</label>
          <div className="relative">
            <Mail size={17} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none z-10" />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="you@company.com"
              data-testid="email-input"
              className={`${authInputClass} pl-9 pr-3`}
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full h-12 rounded-2xl font-bold text-base text-white flex items-center justify-center gap-2 bg-gradient-to-r from-[#57B22E] to-[#3E9A1C] shadow-lg shadow-green-600/30 hover:from-[#4CA327] hover:to-[#358916] active:scale-[0.99] transition-all disabled:opacity-60"
          data-testid="send-reset-button"
        >
          {loading ? (
            <div className="spinner-3d w-6 h-6 border-2"></div>
          ) : (
            <>
              <Send size={20} />
              Send Reset Link
            </>
          )}
        </button>
      </form>

      <button
        onClick={() => navigate('/login')}
        className="mt-4 w-full flex items-center justify-center gap-2 text-slate-600 hover:text-slate-900 font-semibold text-sm"
      >
        <ArrowLeft size={18} />
        Back to Login
      </button>
    </AuthLayout>
  );
}
